import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { getDefaultRouteForRole } from '@/lib/navigation'
import { isModuleAllowedForPlan } from '@/lib/subscriptionPlans'

interface ProtectedRouteProps {
  children: React.ReactNode
  allowedRoles: string[]
}

/**
 * Wrapper component that protects routes based on user role and, for
 * routes gated by MODULE_MIN_PLAN, the organization's subscription plan.
 * If the user doesn't have permission, they're redirected to their default page.
 */
export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth()
  const location = useLocation()

  // Wait for auth to load
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/connexion" replace />
  }

  // If user doesn't have permission, redirect to their default page
  if (!allowedRoles.includes(user.role)) {
    const defaultRoute = getDefaultRouteForRole(user.role)
    return <Navigate to={defaultRoute} replace />
  }

  // If this module isn't included in the organization's plan, same redirect
  if (!isModuleAllowedForPlan(user.organization?.plan ?? null, location.pathname)) {
    const defaultRoute = getDefaultRouteForRole(user.role)
    return <Navigate to={defaultRoute} replace />
  }

  // User has permission, render the page
  return <>{children}</>
}
